import { formatDate } from "date-fns";

import { TabMenu } from "@/types/Shared/tab";
import { ValueType } from "@/types/bid";
import {
  DashboardFilterType,
  GetDashboardBidSummaryContent,
  GetDashboardProjectSummaryContent,
  GetProjectDetailsContent,
} from "@/types/dashboard";

import { subtractMonths } from "@/libs/utils";

export const DashboardTabData: TabMenu[] = [
  { label: "입찰공고", path: "/dashboard/bid" },
  { label: "프로젝트", path: "/dashboard/project" },
];

const currentYear = new Date().getFullYear();

export const DefaultDashboardDateRange = String(currentYear);

export const DashboardDateRangeList: ValueType[] = [
  { label: "YTD", value: "YTD" },
  { label: `${currentYear - 2}년`, value: String(currentYear - 2) },
  { label: `${currentYear - 1}년`, value: String(currentYear - 1) },
  { label: `${currentYear}년`, value: String(currentYear) },
  { label: "자유 입력", value: "0" },
];

// 연도 값인지 확인하는 헬퍼 함수
export const isYearValue = (value: string): boolean => {
  const year = Number(value);
  return !isNaN(year) && year >= 2000 && year <= 2100;
};

// YTD 또는 연도에 따른 시작 날짜 계산
export const getDateRangeStartDate = (dateRange: string): Date => {
  if (dateRange === "YTD") {
    return new Date(new Date().getFullYear(), 0, 1); // 올해 1월 1일
  }
  // 연도인 경우 해당 연도 1월 1일
  if (isYearValue(dateRange)) {
    return new Date(Number(dateRange), 0, 1);
  }
  return subtractMonths(new Date(), Number(dateRange));
};

// YTD 또는 연도에 따른 종료 날짜 계산 (화면 표시용)
export const getDateRangeEndDate = (dateRange: string): Date => {
  if (dateRange === "YTD") {
    return new Date(); // 오늘
  }
  // 연도인 경우 해당 연도 12월 31일 (화면 표시용)
  if (isYearValue(dateRange)) {
    return new Date(Number(dateRange), 11, 31);
  }
  return new Date();
};

// API 전송용 종료 날짜 계산 (연도 필터일 때 내년 1월 1일)
export const getDateRangeEndDateForApi = (dateRange: string): Date => {
  if (dateRange === "YTD") {
    return new Date(); // 오늘
  }
  // 연도인 경우 내년 1월 1일 (API용 - 이상 ~ 미만 처리)
  if (isYearValue(dateRange)) {
    return new Date(Number(dateRange) + 1, 0, 1);
  }
  return new Date();
};

export const DashboardItemTypesList: ValueType[] = [
  { label: "장비", value: "EQUIPMENT" },
  { label: "시약", value: "REAGENT" },
];

export const DashboardBiddingMethodList: ValueType[] = [
  { label: "수의계약", value: "PRIVATE_CONTRACT" },
  { label: "일반경쟁", value: "SUPPLY_CONTRACT" },
  { label: "제한경쟁", value: "PROPOSAL_COMPETITION" },
];

export const DashboardContractMethodsList: ValueType[] = [
  { label: "단가계약", value: "UNIT_PRICE" },
  { label: "총액계약", value: "LUMP_SUM" },
];

export const DashboardParticipationRestrictionsList: ValueType[] = [
  { label: "지역", value: "REGIONAL" },
  { label: "업종", value: "INDUSTRY" },
];

export const DashboardCompanyTypesList: ValueType[] = [
  { label: "TFSS", value: "TFSS" },
  { label: "TFSK", value: "TFSK" },
];

export const DashboardInternationalBidList: ValueType[] = [
  { label: "국제입찰", value: "INTERNATIONAL" },
  { label: "국제입찰 외", value: "DOMESTIC" },
];

export const DefaultDashboardFilter: DashboardFilterType = {
  min_date: formatDate(
    getDateRangeStartDate(DefaultDashboardDateRange).toString(),
    "yyyy-MM-dd"
  ),
  max_date: formatDate(
    getDateRangeEndDate(DefaultDashboardDateRange).toString(),
    "yyyy-MM-dd"
  ),
  date_range: DefaultDashboardDateRange,
  item_types: ["EQUIPMENT", "REAGENT"],
  commodity_ids: [],
  reagent_ids: [],
  dmin_codes: [],
  all_regions: true,
  regions: [],
  bidding_methods: [
    "PRIVATE_CONTRACT",
    "SUPPLY_CONTRACT",
    "PROPOSAL_COMPETITION",
  ],
  contract_methods: ["UNIT_PRICE", "LUMP_SUM"],
  no_budget_limit: true,
  min_budget: 0,
  max_budget: 0,
  participation_restrictions: ["REGIONAL", "INDUSTRY"],
  company_types: ["TFSS", "TFSK", "Others"],
  divisions: [],
  bid_contractor_ids: [],

  contract_company_ids: [],
  project_group_ids: [],
  international_bid: ["INTERNATIONAL", "DOMESTIC"],
  smf_ratio: 0,
};

export const DefaultDashbboardBidSummary: GetDashboardBidSummaryContent = {
  total_biddings: 0, // 총 공고건수
  company_bid_count: 0, // 입찰 참여 공고
  company_contract_count: 0, // 입찰 성공 공고
  total_contract_amount: 0, // 총 계약금액
  company_contract_amount: 0, // 자사계약금액
};

// 대시보드 장비 검색 필터
export const DashboardSearchCommodity = "dashboard.search_commodity";
// 대시보드 시약 검색 필터
export const DashboardSearchReagent = "dashboard.search_reagent";

// 대시보드 디비전 구분 검색 필터
export const DashboardSettingsGetDivisionList =
  "dashboard.settings.get_divisions";

// 대시보드 프로젝트 이름 검색 필터
export const DashboardSearchProjectByTitle =
  "dashboard.search_project_by_title";

// 대시보드 계약기업 이름 검색 필터
export const DashboardSearchCompany = "dashboard.search_company";

export const getDashboardCommonFilterToString = (
  filter: DashboardFilterType
) => {
  const getCommodityIds = () => {
    if (
      !filter.item_types.includes("EQUIPMENT") ||
      filter.commodity_ids === null
    ) {
      return "None";
    }
    return JSON.stringify(filter.commodity_ids.map((item) => item.value));
  };

  const getReagentIds = () => {
    if (!filter.item_types.includes("REAGENT") || filter.reagent_ids === null) {
      return "None";
    }
    return JSON.stringify(filter.reagent_ids.map((item) => item.value));
  };

  // 연도 필터일 때 API용 종료일 (내년 1월 1일) 사용
  const getMaxDateForApi = () => {
    if (isYearValue(filter.date_range)) {
      return formatDate(
        getDateRangeEndDateForApi(filter.date_range).toString(),
        "yyyy-MM-dd"
      );
    }
    return formatDate(new Date(filter.max_date).toString(), "yyyy-MM-dd");
  };

  return (
    `min_date="${formatDate(new Date(filter.min_date).toString(), "yyyy-MM-dd")}", ` +
    `max_date="${getMaxDateForApi()}", ` +
    `commodity_ids=${getCommodityIds()}, ` +
    `reagent_ids=${getReagentIds()}, ` +
    `dmin_codes=${JSON.stringify(filter.dmin_codes.map((item) => item.value))}, ` +
    `all_regions=${filter.all_regions ? "True" : "False"}, ` +
    `regions=${JSON.stringify(filter.regions)}, ` +
    `bidding_methods=${JSON.stringify(filter.bidding_methods)}, ` +
    `contract_methods=${JSON.stringify(filter.contract_methods)}, ` +
    `no_budget_limit=${filter.no_budget_limit ? "True" : "False"}, ` +
    `min_budget=${filter.min_budget}, ` +
    `max_budget=${filter.max_budget}, `
  );
};

export const getDashboardBidFilterToString = (filter: DashboardFilterType) => {
  // 백엔드는 TFSS, TFSK만 허용 - Others는 필터링
  const validCompanyTypes = filter.company_types.filter(
    (type) => type === "TFSS" || type === "TFSK"
  );

  return (
    `DashboardFilterOptions(` +
    getDashboardCommonFilterToString(filter) +
    // 입찰 공고 - 업종제한 여부
    // 업종제한 여부 임시 주석
    // `participation_restrictions=${JSON.stringify(filter.participation_restrictions)}, ` +
    // 입찰 공고 - 자사 법인 구분
    `company_types=${JSON.stringify(validCompanyTypes)}, ` +
    // 입찰 공고 - 국제 입찰 여부
    `international_bid=${JSON.stringify(filter.international_bid)}, ` +
    // 입찰 공고 - 디비전 구분
    `divisions=${JSON.stringify(filter.divisions)})`
  );
};

export const getDashboardProjectFilterToString = (
  filter: DashboardFilterType
) => {
  return (
    `DashboardFilterOptions(` +
    getDashboardCommonFilterToString(filter) +
    // 프로젝트 - 계약 기업 ID 목록 (win_biz_nos로 전송)
    `win_biz_nos=${JSON.stringify(filter.contract_company_ids.map((item) => item.value))}, ` +
    // 프로젝트 - 프로젝트 ID
    `project_group_ids=${JSON.stringify(filter.project_group_ids.map((item) => item.value))}, ` +
    // 프로젝트 - 국제 입찰 여부
    `international_bid=${JSON.stringify(filter.international_bid)}, ` +
    // 프로젝트 - 자사 제품 비중
    `smf_ratio=${filter.smf_ratio})`
  );
};

export const DefaultDashbboardProjectSummary: GetDashboardProjectSummaryContent =
  {
    total_contract_amount: 0, // 계약금액
    base_exchange_rate: 1, // 기준 환율(계약금액 x 환율)
    tpp_margin_rate: 0, // TPP Margin
    smf_margin_rate: 0, // 자사 제품 Margin
    contract_rate: 0, // 계약률
  };

export const DefaultDashboardProjectTargetAchievement: ValueType[] = [
  {
    label: "-", // 연도
    value: "0", // 목표 달성률
  },
];

export const DefaultDashboardProjectTargetAchievementList: ValueType[] =
  Array.from({ length: currentYear - 2020 + 1 }, (_, i) => ({
    label: String(currentYear - i),
    value: "0",
  }));

export const DashboardDefaultProjectTotal: GetProjectDetailsContent = {
  page: 1,
  page_size: 10,
  total_count: 0,
  total_bid_amount: 0,
  total_project_count: 0,
  total_smf_rate: 0,
  total_tpp_rate: 0,
  items: [],
};
